import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { useAppStore } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT } from '../i18n';
import { Icon } from '../icons/Icon';
import { ChangePasswordSheet } from './ChangePasswordSheet';
import { T } from './Text';

/**
 * Who is signed in, and the way out.
 *
 * There was no sign-out control anywhere: the only one lived on the "no access"
 * screen, which nobody working normally ever sees. On a shared counter device
 * that means whoever signed in first stays signed in forever.
 *
 * Confirming on a second tap rather than in a dialog: signing out with unsent
 * writes still in the outbox is recoverable (they stay queued), so a modal
 * would be heavier than the mistake.
 */
export function AccountRow({ compact = false }: { compact?: boolean }) {
  const colors = useColors();
  const t = useT();
  const userEmail = useAppStore((s) => s.userEmail);
  const role = useAppStore((s) => s.role);
  const isPlatformAdmin = useAppStore((s) => s.isPlatformAdmin);
  const pending = useAppStore((s) => s.sync.pending);
  const signOut = useAppStore((s) => s.signOut);
  const [confirm, setConfirm] = useState(false);
  const [changing, setChanging] = useState(false);

  // Working offline on this device with no account: nothing to sign out of.
  if (!userEmail) return null;

  // An administrator holds no post at any restaurant, so `role` is null for
  // them and the restaurant roles do not apply. Checked first: were a stale
  // role left over from a previous session, saying "Worker" under the one
  // account that can rename every shop would be actively misleading.
  const roleLabel = isPlatformAdmin
    ? t('role_admin')
    : role === 'owner'
      ? t('role_owner')
      : role === 'worker'
        ? t('role_worker')
        : null;

  return (
    <View style={{ gap: 8 }}>
      {compact ? null : (
        <View style={{ gap: 2 }}>
          <T size={10.5} weight={700} color={colors.textFaint} style={{ letterSpacing: 0.5 }}>
            {t('signed_in_as').toUpperCase()}
          </T>
          <T size={12.5} weight={600} numberOfLines={1}>
            {userEmail}
          </T>
          {roleLabel ? (
            <T size={11.5} color={colors.textFaint}>
              {roleLabel}
            </T>
          ) : null}
        </View>
      )}

      {/* The early return above means there is a signed-in account by the time
          this renders, so there is always a password to change. */}
      <Pressable
        onPress={() => setChanging(true)}
        testID="change-password"
        accessibilityLabel={t('change_password_title')}
        style={({ hovered }: any) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          paddingVertical: 10,
          paddingHorizontal: 12,
          borderRadius: radius.md,
          borderWidth: 1,
          borderColor: hovered ? colors.gold : colors.line,
        })}
      >
        <Icon name="lock" size={15} color={colors.textDim} />
        <T size={12.5} weight={700} color={colors.textDim}>
          {t('change_password_title')}
        </T>
      </Pressable>

      <ChangePasswordSheet visible={changing} onClose={() => setChanging(false)} />

      <Pressable
        onPress={() => {
          if (!confirm && pending > 0) return setConfirm(true);
          void signOut();
        }}
        testID="sign-out"
        accessibilityLabel={t('sign_out')}
        style={({ hovered }: any) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          paddingVertical: 10,
          paddingHorizontal: 12,
          borderRadius: radius.md,
          borderWidth: 1,
          borderColor: confirm ? colors.spice : hovered ? colors.gold : colors.line,
        })}
      >
        <Icon name="logOut" size={15} color={confirm ? colors.spice : colors.textDim} />
        <T size={12.5} weight={700} color={confirm ? colors.spice : colors.textDim}>
          {/* Says what is at stake instead of a generic "are you sure". */}
          {confirm ? t('sync_pending', { n: pending }) : t('sign_out')}
        </T>
      </Pressable>
    </View>
  );
}
